// Helpers d'affichage du planning — 100 % côté site (styles, libellés éditoriaux, liens).
// Aucune donnée d'horaire, de prof, de salle, de discipline ou d'âge n'est stockée ici :
// tout vient du Google Sheet.

export const timeToMin = (t) => {
  const [h, m] = (t || "0:0").split(":").map(Number);
  return h * 60 + m;
};

// Durée affichée : (1h), (1h30), (45 min)
export function durationLabel(min) {
  if (!min) return "";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `(${m} min)`;
  return m === 0 ? `(${h}h)` : `(${h}h${String(m).padStart(2, "0")})`;
}

// Tranches d'âge proposées par le site. Un cours appartient à une tranche
// dès que son intervalle [Âge min, Âge max] la chevauche.
export const AGE_BUCKETS = {
  petits: { label: "Petits", sub: "2 - 5 ans", min: 2, max: 5 },
  enfants: { label: "Enfants", sub: "6 - 9 ans", min: 6, max: 9 },
  preados: { label: "Pré-ados", sub: "10 - 14 ans", min: 10, max: 14 },
  ados: { label: "Ados", sub: "15 - 17 ans", min: 15, max: 17 },
  adultes: { label: "Adultes", sub: "18 - 64 ans", min: 18, max: 64 },
  seniors: { label: "Seniors", sub: "+65 ans", min: 65, max: Infinity },
};

export function matchesAgeBucket(course, bucketKey) {
  const b = AGE_BUCKETS[bucketKey];
  if (!b) return true;
  if (course.age_min === null || course.age_min === undefined) return true;
  const classMax = course.age_max === null || course.age_max === undefined ? Infinity : course.age_max;
  return course.age_min <= b.max && classMax >= b.min;
}

export function ageBucketsFor(course) {
  return Object.keys(AGE_BUCKETS).filter((k) => matchesAgeBucket(course, k));
}

// Âge de l'élève à partir de sa date de naissance (filtrage des cours dans les formulaires).
export function ageFromBirthDate(iso) {
  if (!iso) return null;
  const b = new Date(iso);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age;
}

// ---------------------------------------------------------------- couleurs (site)
const DISCIPLINE_COLORS = {
  "Hip-Hop": "#14B8C4",
  "Hip-Hop Girly": "#EC4899",
  "Comédie musicale": "#A78BFA",
  Ballet: "#FACC15",
  Eveil: "#FBBF24",
  Compétition: "#0EA5E9",
  Heels: "#F472B6",
  Dancehall: "#FB923C",
  Salsa: "#EF4444",
  Zumba: "#F97316",
  Reggaeton: "#DB2777",
  "Break Dance": "#22D3EE",
  Contemporain: "#8B5CF6",
  Pilates: "#10B981",
  "Parents-enfants": "#34D399",
};
const FALLBACK_PALETTE = ["#14B8C4", "#A78BFA", "#F472B6", "#F97316", "#10B981", "#0EA5E9", "#FACC15", "#EF4444"];

export function disciplineColor(discipline, index = 0) {
  return DISCIPLINE_COLORS[discipline] || FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];
}

// ------------------------------------------------- niveaux : descriptions (site)
export const LEVEL_DESCRIPTIONS = {
  Basique: "0 an d'expérience",
  Débutant: "1-2 ans d'expérience",
  "Débutant avancé": "2-3 ans",
  Intermédiaire: "3-4 ans",
  "Intermédiaire avancé": "4-5 ans",
  Avancé: "5 ans et +",
  Tous: "Aucun prérequis",
};

// ------------------------- lien éditorial cours du planning → fiche /cours/:slug
// La page « Nos cours » reste rédigée à la main : ce résolveur fait le pont entre
// un cours du Sheet et sa fiche descriptive, sans dupliquer d'horaire.
const SLUG_RULES = [
  [/mini[\s-]?(girly|heels)/i, "mini-girly"],
  [/heels/i, "heels"],
  [/girly/i, "girly"],
  [/freestyle/i, "hip-hop-freestyle"],
  [/pr[ée]paration comp[ée]tition/i, "preparation-competition-junior"],
  [/duo.*solo|solo.*duo/i, "duo-solo-ido"],
  [/compagnie/i, "compagnie-junior"],
  [/varsity/i, "competition-varsity"],
  [/comp[ée]tition/i, "competition-junior"],
  [/com[ée]die musicale/i, "comedie-musicale"],
  [/break/i, "break-dance"],
  [/hip[\s-]?hop/i, "hip-hop"],
  [/baby ballet|ballet/i, "ballet-eveil"],
  [/classique/i, "classique"],
  [/[ée]veil/i, "eveil-danse"],
  [/parents[\s-]?enfants/i, "danse-parents-enfants"],
  [/contemporain/i, "contemporain"],
  [/dancehall/i, "dancehall"],
  [/salsa/i, "salsa"],
  [/zumba/i, "zumba"],
  [/reggaeton/i, "reggaeton"],
];

export function courseSlug(course) {
  const haystack = `${course?.name || ""} ${course?.discipline || ""}`;
  for (const [re, slug] of SLUG_RULES) {
    if (re.test(haystack)) return slug;
  }
  return null;
}

export const SEAT_STATES = {
  OUVERT: { bookable: true, urgent: false },
  DERNIERES_PLACES: { bookable: true, urgent: true },
  COMPLET: { bookable: false, urgent: true, waitlist: true },
  FERME: { bookable: false, urgent: false, hidden: true },
};

export function seatState(course) {
  return SEAT_STATES[(course?.etat_places || "OUVERT").toUpperCase()] || SEAT_STATES.OUVERT;
}

// Décision de l'école du 02.11.2026 : pas de cours d'essai dans un cours complet (planning,
// page du cours, formulaire d'essai ; le serveur refuse aussi). La liste d'attente reste.
export const MESSAGE_ESSAI_COMPLET =
  "Ce cours est complet : pas de cours d'essai pour le moment. Inscris-toi en liste d'attente, on t'écrit dès qu'une place se libère.";
